// src/belediye/pages/dashboard/components/DashboardLeafletMap.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import belediyeApi from "../../../services/belediyeApi";
import "./DashboardLeafletMap.css";

/* ─────────────────────────────────────────────────────────────
   Constants
───────────────────────────────────────────────────────────── */
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

const CARTO_TILE_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const CARTO_ATTRIBUTION = '&copy; <a href="https://carto.com/">CARTO</a>';

const DEFAULT_CENTER = [39.0, 35.0];
const DEFAULT_ZOOM = 6;

const STATUS_COLORS = {
  yeni: "#22c55e",
  işleme_alındı: "#3b82f6",
  çözüldü: "#14b8a6",
  çözülemedi: "#ef4444",
  default: "#6b7280",
};

const STATUS_LABELS = {
  yeni: "Yeni",
  işleme_alındı: "İncelemede",
  çözüldü: "Çözüldü",
  çözülemedi: "Çözülemedi",
};

/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
function statusColor(durum) {
  return STATUS_COLORS[durum] || STATUS_COLORS.default;
}

function statusLabel(durum) {
  return STATUS_LABELS[durum] || String(durum || "Bilinmiyor");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function sayiFormat(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("tr-TR").format(n);
}

function trTarihSaat(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function normalizeCenter(raw) {
  if (!raw) return null;

  if (Array.isArray(raw) && raw.length >= 2) {
    const lat = Number(raw[0]);
    const lng = Number(raw[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return null;
  }

  if (typeof raw === "object") {
    const lat = Number(raw.lat ?? raw.latitude ?? raw.center_lat ?? raw.enlem);
    const lng = Number(
      raw.lng ?? raw.longitude ?? raw.center_lng ?? raw.boylam ?? raw.lon
    );

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }

  return null;
}

function normalizeZoom(raw) {
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function normalizePointCoords(nokta) {
  const lat = Number(nokta?.enlem ?? nokta?.latitude ?? nokta?.lat);
  const lng = Number(
    nokta?.boylam ?? nokta?.longitude ?? nokta?.lng ?? nokta?.lon
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function normalizeBoundaryId(raw) {
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function buildMarkerIcon(L, color) {
  return L.divIcon({
    className: "dlm-custom-marker",
    html: `
      <div style="
        width:14px;
        height:14px;
        border-radius:50%;
        background:${color};
        border:3px solid rgba(255,255,255,0.96);
        box-shadow:0 3px 10px rgba(15,23,42,0.22);
      "></div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });
}

function buildBoundaryStyle(boundary, isSelected = false) {
  const style = boundary?.style || {};
  const displayMode = style.display_mode || boundary?.display_mode || "both";

  const borderColor = style.border_color || boundary?.border_color || "#1f2a40";
  const fillColor = style.fill_color || boundary?.fill_color || "#314a6b";
  const fillOpacity = Number(
    style.fill_opacity ?? boundary?.fill_opacity ?? 0.1
  );
  const strokeWidth = Number(style.stroke_width ?? boundary?.stroke_width ?? 2);

  return {
    color: borderColor,
    weight: isSelected ? Math.max(strokeWidth + 1, 3) : Math.max(strokeWidth, 1),
    opacity: 0.9,
    fill: displayMode !== "line",
    fillColor,
    fillOpacity: displayMode === "line" ? 0 : fillOpacity,
  };
}

function boundaryPopupHtml(boundary) {
  const centerLat =
    boundary?.center?.lat ?? boundary?.center_lat ?? boundary?.enlem;
  const centerLng =
    boundary?.center?.lng ?? boundary?.center_lng ?? boundary?.boylam;

  return `
    <div class="dlm-popup">
      <div class="dlm-popup-title">${escapeHtml(boundary?.name || "Sınır")}</div>
      ${
        boundary?.population != null
          ? `<div class="dlm-popup-meta">Nüfus: ${sayiFormat(boundary.population)}</div>`
          : ""
      }
      ${
        boundary?.area_km2 != null
          ? `<div class="dlm-popup-meta">Alan: ${escapeHtml(boundary.area_km2)} km²</div>`
          : ""
      }
      ${
        centerLat != null && centerLng != null
          ? `<div class="dlm-popup-mono">${Number(centerLat).toFixed(5)}, ${Number(
              centerLng
            ).toFixed(5)}</div>`
          : ""
      }
    </div>
  `;
}

function pointPopupHtml(nokta, color) {
  return `
    <div class="dlm-popup">
      <div class="dlm-popup-title">${escapeHtml(
        nokta?.baslik || nokta?.takipNo || "Başvuru"
      )}</div>
      <div class="dlm-popup-row">
        <span class="dlm-popup-dot" style="background:${color}"></span>
        <span>${escapeHtml(statusLabel(nokta?.durum))}</span>
      </div>
      ${
        nokta?.birimAdi
          ? `<div class="dlm-popup-meta">Birim: ${escapeHtml(nokta.birimAdi)}</div>`
          : ""
      }
      ${
        nokta?.mahalleAdi
          ? `<div class="dlm-popup-meta">Mahalle: ${escapeHtml(
              nokta.mahalleAdi
            )}</div>`
          : ""
      }
      ${
        nokta?.tarih
          ? `<div class="dlm-popup-meta">${escapeHtml(
              trTarihSaat(nokta.tarih)
            )}</div>`
          : ""
      }
      ${
        nokta?.takipNo
          ? `<div class="dlm-popup-mono">${escapeHtml(nokta.takipNo)}</div>`
          : ""
      }
    </div>
  `;
}

function buildLegendItems(durumOzeti = [], noktalar = []) {
  if (Array.isArray(durumOzeti) && durumOzeti.length) {
    return durumOzeti
      .filter((item) => item?.durum)
      .map((item) => ({
        durum: item.durum,
        etiket: statusLabel(item.durum),
        sayi: Number(item?.sayi || 0),
        renk: statusColor(item.durum),
      }));
  }

  const counts = new Map();

  (Array.isArray(noktalar) ? noktalar : []).forEach((n) => {
    const durum = n?.durum || "default";
    counts.set(durum, (counts.get(durum) || 0) + 1);
  });

  return Array.from(counts.entries()).map(([durum, sayi]) => ({
    durum,
    etiket: statusLabel(durum),
    sayi,
    renk: statusColor(durum),
  }));
}

/* ─────────────────────────────────────────────────────────────
   Leaflet loader
───────────────────────────────────────────────────────────── */
let leafletPromise = null;

function loadLeaflet() {
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    if (window.L) {
      resolve(window.L);
      return;
    }

    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.defer = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error("Leaflet yüklenemedi"));
    document.head.appendChild(script);
  });

  return leafletPromise;
}

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
export default function DashboardLeafletMap({
  noktalar = [],
  durumOzeti = [],
  height = 340,
  className = "",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const boundaryLayerRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const initialViewAppliedRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const [bm4Loading, setBm4Loading] = useState(false);
  const [defaultResolved, setDefaultResolved] = useState(false);
  const [boundariesResolved, setBoundariesResolved] = useState(false);
  const [bm4DefaultConfig, setBm4DefaultConfig] = useState(null);
  const [boundaries, setBoundaries] = useState([]);
  const [selectedBoundaryId, setSelectedBoundaryId] = useState(null);

  const legendItems = useMemo(
    () => buildLegendItems(durumOzeti, noktalar),
    [durumOzeti, noktalar]
  );

  const defaultCenter = useMemo(
    () => normalizeCenter(bm4DefaultConfig?.center || bm4DefaultConfig),
    [bm4DefaultConfig]
  );

  const defaultZoom = useMemo(
    () => normalizeZoom(bm4DefaultConfig?.zoom_level ?? bm4DefaultConfig?.zoom),
    [bm4DefaultConfig]
  );

  const selectedBoundary = useMemo(() => {
    const targetId = normalizeBoundaryId(selectedBoundaryId);
    if (!targetId) return null;
    return boundaries.find((b) => normalizeBoundaryId(b?.id) === targetId) || null;
  }, [boundaries, selectedBoundaryId]);

  const safeStopMap = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    try {
      if (typeof map.stop === "function") map.stop();
    } catch {
      //
    }
  }, []);

  const safeSetView = useCallback(
    (center, zoom) => {
      const map = mapRef.current;
      const normalizedCenter = normalizeCenter(center);
      const normalizedZoom = normalizeZoom(zoom);

      if (!map || !normalizedCenter) return false;

      safeStopMap();

      try {
        map.setView(normalizedCenter, normalizedZoom ?? map.getZoom(), {
          animate: false,
        });
        return true;
      } catch {
        return false;
      }
    },
    [safeStopMap]
  );

  const safeFitToGeoJson = useCallback(
    (geojson, maxZoom = 15) => {
      const map = mapRef.current;
      if (!map || !geojson || !window.L) return false;

      try {
        const temp = window.L.geoJSON(geojson);
        const bounds = temp.getBounds?.();
        if (!bounds || !bounds.isValid()) return false;

        safeStopMap();
        map.fitBounds(bounds, {
          padding: [28, 28],
          maxZoom,
          animate: false,
        });
        return true;
      } catch {
        return false;
      }
    },
    [safeStopMap]
  );

  const safeFitToPoints = useCallback(
    (points) => {
      const map = mapRef.current;
      if (!map || !window.L) return false;

      const latLngs = (points || []).map(normalizePointCoords).filter(Boolean);
      if (!latLngs.length) return false;

      safeStopMap();

      try {
        if (latLngs.length === 1) {
          map.setView(latLngs[0], 14, { animate: false });
          return true;
        }

        map.fitBounds(window.L.latLngBounds(latLngs), {
          padding: [32, 32],
          maxZoom: 14,
          animate: false,
        });
        return true;
      } catch {
        return false;
      }
    },
    [safeStopMap]
  );

  const fetchBm4Data = useCallback(async () => {
    try {
      setBm4Loading(true);
      setDefaultResolved(false);
      setBoundariesResolved(false);

      const { data } = await belediyeApi.get("/bm4/boundaries/default/");
      if (isUnmountedRef.current) return;

      const nextDefault = data?.default || null;
      setBm4DefaultConfig(nextDefault);

      const defaultBoundaryId = nextDefault?.id ?? null;
      const level = nextDefault?.level || "ilce";

      if (defaultBoundaryId != null) {
        setSelectedBoundaryId(defaultBoundaryId);
      }

      setDefaultResolved(true);

      const boundaryResp = await belediyeApi.get("/bm4/boundaries/", {
        params: {
          level,
          with_geojson: true,
          only_active: true,
        },
      });

      if (isUnmountedRef.current) return;

      const results = Array.isArray(boundaryResp?.data?.results)
        ? boundaryResp.data.results
        : [];

      setBoundaries(results);
      setBoundariesResolved(true);
    } catch (e) {
      if (isUnmountedRef.current) return;

      setError(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          "Harita verisi alınamadı."
      );

      setDefaultResolved(true);
      setBoundariesResolved(true);
    } finally {
      if (!isUnmountedRef.current) {
        setBm4Loading(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadLeaflet()
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    isUnmountedRef.current = false;
    fetchBm4Data();

    return () => {
      isUnmountedRef.current = true;
    };
  }, [fetchBm4Data]);

  useEffect(() => {
    if (!ready || !containerRef.current || mapRef.current) return;

    const L = window.L;

    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      attributionControl: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    });

    L.tileLayer(CARTO_TILE_URL, {
      attribution: CARTO_ATTRIBUTION,
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);

    markerLayerRef.current = L.layerGroup().addTo(map);
    boundaryLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Konteyner (ör. tam ekran) tam oturmadan init olduysa: bir sonraki frame'de
    // boyutu yeniden hesapla → tile/marker yatay kayması önlenir. pan:false ile
    // görünüm kaydırılmaz.
    requestAnimationFrame(() => {
      try {
        map.invalidateSize({ animate: false, pan: false });
      } catch {
        //
      }
    });

    return () => {
      try {
        map.off();
        map.remove();
      } catch {
        //
      }
      mapRef.current = null;
      markerLayerRef.current = null;
      boundaryLayerRef.current = null;
    };
  }, [ready]);

  useEffect(() => {
    if (!ready || !boundaryLayerRef.current || !window.L) return;

    const layerGroup = boundaryLayerRef.current;
    layerGroup.clearLayers();

    boundaries.forEach((boundary) => {
      if (!boundary?.geojson) return;

      const isSelected =
        normalizeBoundaryId(boundary?.id) === normalizeBoundaryId(selectedBoundaryId);

      const geoJsonLayer = window.L.geoJSON(boundary.geojson, {
        style: () => buildBoundaryStyle(boundary, isSelected),
        onEachFeature: (_feature, layer) => {
          layer.bindPopup(boundaryPopupHtml(boundary), {
            maxWidth: 260,
            className: "dlm-leaflet-popup",
          });

          layer.on("click", () => {
            setSelectedBoundaryId(boundary?.id ?? null);
            if (boundary?.geojson) {
              safeFitToGeoJson(boundary.geojson);
            }
          });
        },
      });

      geoJsonLayer.addTo(layerGroup);

      if (isSelected && typeof geoJsonLayer.bringToFront === "function") {
        geoJsonLayer.bringToFront();
      }
    });
  }, [ready, boundaries, selectedBoundaryId, safeFitToGeoJson]);

  useEffect(() => {
    if (!ready || !markerLayerRef.current || !window.L) return;

    const markerLayer = markerLayerRef.current;
    markerLayer.clearLayers();

    (Array.isArray(noktalar) ? noktalar : []).forEach((nokta) => {
      const coords = normalizePointCoords(nokta);
      if (!coords) return;

      const color = statusColor(nokta?.durum);
      const marker = window.L.marker(coords, {
        icon: buildMarkerIcon(window.L, color),
      });

      marker.bindPopup(pointPopupHtml(nokta, color), {
        maxWidth: 240,
        className: "dlm-leaflet-popup",
      });

      marker.addTo(markerLayer);
    });
  }, [ready, noktalar]);

  /* İlk açılış: center varsa onu kullan; yoksa default boundary geojson; yoksa ilk boundary; en son points */
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    if (!defaultResolved || !boundariesResolved) return;
    if (initialViewAppliedRef.current) return;

    if (defaultCenter && safeSetView(defaultCenter, defaultZoom ?? DEFAULT_ZOOM)) {
      initialViewAppliedRef.current = true;
      return;
    }

    if (selectedBoundary?.geojson && safeFitToGeoJson(selectedBoundary.geojson)) {
      initialViewAppliedRef.current = true;
      return;
    }

    const firstBoundaryWithGeoJson = boundaries.find((b) => b?.geojson);
    if (firstBoundaryWithGeoJson?.geojson && safeFitToGeoJson(firstBoundaryWithGeoJson.geojson)) {
      initialViewAppliedRef.current = true;
      return;
    }

    if (Array.isArray(noktalar) && noktalar.length && safeFitToPoints(noktalar)) {
      initialViewAppliedRef.current = true;
      return;
    }

    if (safeSetView(DEFAULT_CENTER, DEFAULT_ZOOM)) {
      initialViewAppliedRef.current = true;
    }
  }, [
    ready,
    defaultResolved,
    boundariesResolved,
    defaultCenter,
    defaultZoom,
    selectedBoundary,
    boundaries,
    noktalar,
    safeSetView,
    safeFitToGeoJson,
    safeFitToPoints,
  ]);

  useEffect(() => {
    if (!mapRef.current) return;

    const timer = setTimeout(() => {
      try {
        mapRef.current?.invalidateSize({ animate: false, pan: false });
      } catch {
        //
      }
    }, 120);

    return () => clearTimeout(timer);
  }, [height, noktalar, boundaries.length]);

  /* Konteyner boyutu değişince (ör. tam ekran geçişi) haritayı yeniden ölç —
     aksi halde tile/marker'lar kayar. ResizeObserver + rAF debounce. */
  useEffect(() => {
    if (!ready || !containerRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const el = containerRef.current;
    let rafId = 0;

    const observer = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        try {
          mapRef.current?.invalidateSize({ animate: false, pan: false });
        } catch {
          //
        }
      });
    });

    observer.observe(el);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [ready]);

  if (error) {
    return (
      <div className={`dlm-root dlm-root--error ${className}`} style={{ height }}>
        <div className="dlm-error">
          <span className="dlm-error-icon">⚠</span>
          <span>Harita yüklenemedi</span>
          <small>{error}</small>
        </div>
      </div>
    );
  }

  return (
    <div className={`dlm-root ${className}`} style={{ height }}>
      <div className="dlm-map-container">
        {!ready && (
          <div className="dlm-loading">
            <div className="dlm-spinner" />
            <span>Harita yükleniyor…</span>
          </div>
        )}

        {ready && bm4Loading && !boundaries.length && (
          <div className="dlm-loading">
            <div className="dlm-spinner" />
            <span>Sınırlar yükleniyor…</span>
          </div>
        )}

        <div
          ref={containerRef}
          className="dlm-map"
          style={{ opacity: ready ? 1 : 0 }}
        />

        {!!legendItems.length && (
          <div className="dlm-legend" aria-label="Harita lejantı">
            <div className="dlm-legend-list">
              {legendItems.map((item) => (
                <div key={item.durum} className="dlm-legend-item">
                  <span
                    className="dlm-legend-dot"
                    style={{ background: item.renk }}
                  />
                  <span className="dlm-legend-label">{item.etiket}</span>
                  <span className="dlm-legend-value">
                    {sayiFormat(item.sayi)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}